import { createStore, type StoreApi } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  createInitialPublishOrderState,
  createPublishOrderActions,
  type PublishOrderActions,
  type PublishOrderState,
  type PublishOrderStore,
} from '../usePublishOrderStore';
import type { OrderDraftMode } from './types';

// `createPublishOrderActions` is typed against the singleton's `set`
// signature (`PublishOrderStore`). The scoped store carries extra
// metadata (`mode`, `sourceOrderId`) but the action map only touches
// fields present on the base store, so the cast widens nothing the
// runtime would see.
type OrderDraftSet = Parameters<
  Parameters<typeof immer<PublishOrderStore>>[0]
>[0];

/**
 * Options accepted by {@link createOrderDraftStore}. Every field is
 * optional — the factory falls back to the same defaults as the
 * legacy `usePublishOrderStore` singleton.
 */
export interface CreateOrderDraftStoreOptions {
  /** Tags the draft so commit hooks know what mutation to dispatch. */
  mode?: OrderDraftMode;
  /** Populated by `fromOrder` / `fromOrderAgainSource` for edit / reorder flows. */
  sourceOrderId?: string;
  /** Partial hydration patch applied on top of the empty defaults. */
  initialState?: Partial<PublishOrderState>;
}

/**
 * Metadata fields attached to every store created via the factory.
 * They live alongside the draft state so consumers can branch on
 * them without an extra context lookup.
 */
export interface OrderDraftMeta {
  mode: OrderDraftMode;
  sourceOrderId?: string;
}

export type OrderDraftStore = PublishOrderStore & OrderDraftMeta;

/**
 * Factory variant of `usePublishOrderStore`. Each call produces a
 * fresh, isolated store — used by `OrderDraftProvider` to scope a
 * draft to a single agent confirmation card / reorder modal / edit
 * drawer instance.
 *
 * The legacy module-singleton at `usePublishOrderStore` is kept as
 * the default instance for the publish page so existing imports
 * keep working unchanged.
 */
export const createOrderDraftStore = (
  options: CreateOrderDraftStoreOptions = {},
): StoreApi<OrderDraftStore> => {
  const meta: OrderDraftMeta = {
    mode: options.mode ?? 'create',
    sourceOrderId: options.sourceOrderId,
  };
  const seeded: PublishOrderState = {
    ...createInitialPublishOrderState(),
    ...(options.initialState ?? {}),
  };

  return createStore<OrderDraftStore>()(
    immer(set => ({
      ...seeded,
      ...createPublishOrderActions(set as OrderDraftSet),
      ...meta,
    })),
  );
};

export type { PublishOrderActions, PublishOrderState };
